import { BookmarkXIcon, DownloadIcon, FilterXIcon, SaveIcon } from 'lucide-react';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@/shared/ui';

/** Пропы панели действий над списком диалогов */
export type DialogListToolbarProps = {
  /** Есть донастройка поверх слоя (или обычные фильтры) — показывать кнопку сброса */
  canReset: boolean;
  /** Сброс донастройки к базе слоя (пресету/выборке) или к полному списку, если слоя нет */
  onResetFilters: () => void;
  /** Слой отбора донастроен — показывать кнопку записи донастройки в него */
  canSaveLayer: boolean;
  /** Название слоя отбора (пресета, выборки) — подставляется в подсказку кнопки сохранения */
  layerName?: string;
  /** Записать текущую донастройку в слой отбора */
  onSaveLayer: () => void;
  /** Слой отбора можно снять — показывать кнопку выхода из него */
  canExitLayer: boolean;
  /** Снятие слоя отбора: список возвращается к виду без него */
  onExitLayer: () => void;
  /** Блок слоя отбора: пресеты в разделе диалогов, выборки в разделе выборок */
  layerBlock: ReactNode;
  /** Выгрузка текущего списка файлом */
  onDownloadList: () => void;
  /** Файл списка формируется — повторное скачивание не запускается */
  isDownloadPending: boolean;
};

/**
 * Панель начинается с плашки — кнопки слоя отбора с рамкой, — когда ни одна кнопка-иконка
 * левее неё не показана. Признак нужен строке управления для выравнивания левого края блоков и
 * живёт рядом с кнопками, чтобы их добавление или удаление не ломало выравнивание молча.
 */
export const isToolbarStartPlated = ({
  canReset,
  canSaveLayer,
  canExitLayer,
  layerName,
}: Pick<DialogListToolbarProps, 'canReset' | 'canSaveLayer' | 'canExitLayer' | 'layerName'>): boolean =>
  Boolean(layerName) && !canReset && !canSaveLayer && !canExitLayer;

/**
 * Панель действий над списком диалогов в строке поиска: сброс донастройки, запись её в слой
 * отбора, снятие слоя, сам блок слоя (пресеты или выборки) и выгрузка списка файлом.
 * Кнопки-иконки показываются по соответствующему признаку доступности.
 */
export const DialogListToolbar: FC<DialogListToolbarProps> = ({
  canReset,
  onResetFilters,
  canSaveLayer,
  layerName,
  onSaveLayer,
  canExitLayer,
  onExitLayer,
  layerBlock,
  onDownloadList,
  isDownloadPending,
}) => {
  const { t } = useTranslation(['dialogs', 'tablePresets', 'common']);

  return (
    <div className="flex items-center gap-2 min-w-0">
      {canReset && (
        // Только иконка: название выводится в подсказке при наведении.
        <IconButton
          variant="ghost"
          size="icon"
          className="shrink-0"
          label={t('dialogs:dialogListSection.resetFilters.label')}
          icon={<FilterXIcon className="size-4" />}
          onClick={onResetFilters}
        />
      )}
      {canSaveLayer && (
        // Парная кнопке отмены изменений: записывает донастройку в слой отбора из строки
        // поиска, без открытия выпадающего списка.
        <IconButton
          variant="ghost"
          size="icon"
          className="shrink-0"
          label={t('tablePresets:saveToActive', { name: layerName })}
          icon={<SaveIcon className="size-4" />}
          onClick={onSaveLayer}
        />
      )}
      {canExitLayer && (
        // Снятие слоя отбора: выборки перехода в разделе диалогов, выбранной выборки в её разделе.
        <IconButton
          variant="ghost"
          size="icon"
          className="shrink-0"
          label={t('dialogs:dialogListSection.deselectSelection.label')}
          icon={<BookmarkXIcon className="size-4" />}
          onClick={onExitLayer}
        />
      )}
      {layerBlock}
      {/* Формат один — нажатие сразу начинает скачивание, выбирать пользователю нечего. */}
      <IconButton
        variant="ghost"
        size="icon"
        className="shrink-0"
        label={t('dialogs:dialogListSection.downloadList')}
        disabled={isDownloadPending}
        icon={<DownloadIcon className="size-4" />}
        onClick={onDownloadList}
      />

    </div>
  );
};
